/**
 * compoundData.js
 * Static educational data for all cannabinoids and terpenes tracked in the DYC COA system.
 * Keyed by the exact `key` value from coaFieldConfig.js so navigation via slug is direct.
 *
 * Receptor affinity: "None" | "Low" | "Moderate" | "High" | "Very High" | "Variable"
 */

const compoundData = {

    // ─── MAJOR CANNABINOIDS ────────────────────────────────────────────────────

    THCa: {
        slug: "THCa",
        name: "Tetrahydrocannabinolic Acid",
        abbreviation: "THCa",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₂₂H₃₀O₄",
        molecularWeight: "358.47 g/mol",
        boilingPoint: "105°C (221°F)",
        cas: "25304-81-8",
        psychoactive: false,
        description: "THCa is the non-psychoactive acidic precursor to Delta-9 THC found abundantly in fresh, undried cannabis. When exposed to heat (combustion, vaporization, or baking) it decarboxylates — losing a carboxyl group — and converts to the psychoactive Delta-9 THC. In its raw form, THCa has emerged as a compound of significant research interest for its anti-inflammatory and neuroprotective properties, without producing the intoxicating effects associated with its converted form.",
        effects: ["Anti-inflammatory", "Neuroprotective", "Antiemetic", "Antiproliferative"],
        aroma: [],
        foundIn: ["Raw cannabis flower", "Fresh cannabis juice"],
        receptors: { CB1: "Low", CB2: "Low" },
        researchAreas: ["Neuroinflammation", "Nausea", "Seizure disorders", "Metabolic disorders"],
        relatedCompounds: ["delta_9_THC", "Myrcene", "beta_caryophyllene"],
        accentColor: "#007c45",
    },

    delta_9_THC: {
        slug: "delta_9_THC",
        name: "Delta-9 Tetrahydrocannabinol",
        abbreviation: "D9-THC",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₂₁H₃₀O₂",
        molecularWeight: "314.46 g/mol",
        boilingPoint: "157°C (315°F)",
        cas: "1972-08-3",
        psychoactive: true,
        description: "Delta-9 THC is the primary psychoactive compound in cannabis and the most studied cannabinoid in existence. It acts as a partial agonist at both CB1 and CB2 receptors of the endocannabinoid system, with its psychoactive effects primarily mediated through CB1 receptors concentrated in the brain and central nervous system. Beyond its well-known intoxicating effects, D9-THC has established medical applications including FDA-approved formulations (dronabinol) for chemotherapy-induced nausea and AIDS-related weight loss. Its effects on pain perception, appetite stimulation, and mood modulation make it one of the most pharmacologically significant plant-derived compounds ever identified.",
        effects: ["Euphoria", "Analgesic", "Appetite stimulation", "Anti-nausea", "Anxiolytic (low dose)", "Anxiogenic (high dose)"],
        aroma: [],
        foundIn: ["Cannabis flower", "Hashish", "Cannabis concentrates"],
        receptors: { CB1: "High", CB2: "Moderate" },
        researchAreas: ["Pain management", "Nausea & vomiting", "Appetite stimulation", "Multiple sclerosis", "PTSD"],
        relatedCompounds: ["THCa", "CBN", "Myrcene", "Linalool", "beta_caryophyllene"],
        accentColor: "#007c45",
    },

    D8_THC: {
        slug: "D8_THC",
        name: "Delta-8 Tetrahydrocannabinol",
        abbreviation: "D8-THC",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₂₁H₃₀O₂",
        molecularWeight: "314.46 g/mol",
        boilingPoint: "175°C (347°F)",
        cas: "5957-75-5",
        psychoactive: true,
        description: "Delta-8 THC is a structural isomer of Delta-9 THC, differing only in the position of a double bond on the carbon chain (at the 8th position rather than the 9th). It occurs naturally in cannabis in trace amounts, but most commercial D8-THC is produced semi-synthetically by isomerizing CBD. Users and researchers report it produces a milder, more clear-headed psychoactive experience compared to D9-THC, with less associated anxiety and paranoia. Its anti-nausea properties have been of particular interest to pediatric oncology researchers.",
        effects: ["Mild euphoria", "Anti-nausea", "Anxiolytic", "Analgesic", "Appetite stimulation"],
        aroma: [],
        foundIn: ["Cannabis (trace amounts)", "Semi-synthetic production from CBD"],
        receptors: { CB1: "Moderate", CB2: "Low" },
        researchAreas: ["Anti-nausea", "Anxiety", "Neuroprotection", "Pain"],
        relatedCompounds: ["delta_9_THC", "THCa", "CBD"],
        accentColor: "#405c6a",
    },

    THCV: {
        slug: "THCV",
        name: "Tetrahydrocannabivarin",
        abbreviation: "THCV",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₁₉H₂₆O₂",
        molecularWeight: "286.41 g/mol",
        boilingPoint: "220°C (428°F)",
        cas: "31262-37-0",
        psychoactive: true,
        description: "THCV is a propyl analog of THC — structurally identical but with a 3-carbon propyl chain instead of a 5-carbon pentyl chain. Its behavior at cannabinoid receptors is uniquely dose-dependent: at low doses it acts as a CB1 antagonist (potentially suppressing appetite and attenuating THC's effects), while at high doses it behaves as a CB1 agonist producing psychoactive effects. This dual activity makes THCV one of the most pharmacologically interesting cannabinoids for metabolic disorders and diabetes research. It is found in higher concentrations in African sativa landrace cultivars.",
        effects: ["Appetite suppression (low dose)", "Psychoactive (high dose)", "Anti-diabetic", "Bone growth stimulation"],
        aroma: [],
        foundIn: ["African sativa landrace cannabis"],
        receptors: { CB1: "Variable", CB2: "Moderate" },
        researchAreas: ["Diabetes / insulin resistance", "Obesity", "Bone health", "Epilepsy"],
        relatedCompounds: ["delta_9_THC", "THCVa", "CBD"],
        accentColor: "#405c6a",
    },

    THCVa: {
        slug: "THCVa",
        name: "Tetrahydrocannabivarinic Acid",
        abbreviation: "THCVa",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₁₉H₂₄O₄",
        molecularWeight: "320.38 g/mol",
        boilingPoint: "—",
        cas: "39986-26-0",
        psychoactive: false,
        description: "THCVa is the acidic, non-psychoactive precursor to THCV. Like all acid-form cannabinoids, it converts to its active counterpart upon decarboxylation through heat. It is biosynthesized from divarinic acid in the cannabis plant and is present primarily in African sativa varieties. Research on THCVa specifically is limited, but its role as a precursor and potential anti-inflammatory properties make it a compound of emerging interest.",
        effects: ["Anti-inflammatory", "Precursor to THCV"],
        aroma: [],
        foundIn: ["African sativa cannabis"],
        receptors: { CB1: "None", CB2: "Low" },
        researchAreas: ["Inflammation", "Precursor chemistry"],
        relatedCompounds: ["THCV", "THCa", "CBDVA"],
        accentColor: "#405c6a",
    },

    CBDA: {
        slug: "CBDA",
        name: "Cannabidiolic Acid",
        abbreviation: "CBDa",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₂₂H₃₀O₄",
        molecularWeight: "358.47 g/mol",
        boilingPoint: "—",
        cas: "1244-58-2",
        psychoactive: false,
        description: "CBDa is the acidic precursor to CBD and the most abundant cannabinoid in high-CBD cannabis cultivars before decarboxylation. It is produced by the plant through enzymatic oxidation of CBGa. Research suggests CBDa may have greater bioavailability than CBD in some delivery methods and shows potent antiemetic activity by interacting with 5-HT1A serotonin receptors — a mechanism distinct from CBD's pathways. It is also being studied as a potential antiviral agent and for its anti-inflammatory COX-2 enzyme inhibition.",
        effects: ["Anti-nausea", "Anti-inflammatory", "Antibacterial", "Antiproliferative"],
        aroma: [],
        foundIn: ["Raw high-CBD cannabis", "Hemp flower"],
        receptors: { CB1: "None", CB2: "Low" },
        researchAreas: ["Nausea & vomiting", "Inflammation", "Cancer", "Anxiety"],
        relatedCompounds: ["CBD", "CBGa", "THCa"],
        accentColor: "#007c45",
    },

    CBD: {
        slug: "CBD",
        name: "Cannabidiol",
        abbreviation: "CBD",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₂₁H₃₀O₂",
        molecularWeight: "314.46 g/mol",
        boilingPoint: "160–180°C (320–356°F)",
        cas: "13956-29-1",
        psychoactive: false,
        description: "CBD is the second most researched cannabinoid and the only one with an FDA-approved pharmaceutical formulation (Epidiolex) for treatment-resistant epilepsy. Unlike THC, CBD has negligible affinity for CB1 and CB2 receptors in their active state, instead modulating the endocannabinoid system through allosteric mechanisms, inhibiting enzymatic breakdown of endocannabinoids, and acting on numerous non-cannabinoid receptors including 5-HT1A, TRPV1, and GPR55. Its anxiolytic, anti-inflammatory, and anticonvulsant properties are well-documented across dozens of clinical trials. CBD is also notable for attenuating some of the anxiety- and paranoia-inducing effects of THC when co-administered.",
        effects: ["Anxiolytic", "Anti-inflammatory", "Anticonvulsant", "Antipsychotic", "Analgesic", "Neuroprotective"],
        aroma: [],
        foundIn: ["Hemp", "High-CBD cannabis cultivars"],
        receptors: { CB1: "None", CB2: "Low" },
        researchAreas: ["Epilepsy", "Anxiety", "Pain", "Inflammatory bowel disease", "Psychosis", "Sleep"],
        relatedCompounds: ["CBDA", "CBG", "CBN", "Linalool", "Limonene"],
        accentColor: "#007c45",
    },

    CBDV: {
        slug: "CBDV",
        name: "Cannabidivarin",
        abbreviation: "CBDV",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₁₉H₂₆O₂",
        molecularWeight: "286.41 g/mol",
        boilingPoint: "—",
        cas: "24274-48-4",
        psychoactive: false,
        description: "CBDV is the propyl analog of CBD, sharing a similar pharmacological profile but with a 3-carbon side chain. It has been of particular interest to neurological researchers for its anticonvulsant properties. GW Pharmaceuticals (now Jazz Pharmaceuticals) has investigated CBDV as a treatment for autism spectrum disorder and Rett syndrome. Like CBD, it is non-psychoactive and appears to modulate TRPV1 channels and desensitize acid-sensing ion channels involved in seizure activity.",
        effects: ["Anticonvulsant", "Anti-nausea", "Anti-inflammatory"],
        aroma: [],
        foundIn: ["Cannabis (trace amounts)", "Hemp"],
        receptors: { CB1: "None", CB2: "Low" },
        researchAreas: ["Epilepsy", "Autism spectrum disorder", "Rett syndrome", "Nausea"],
        relatedCompounds: ["CBD", "CBDVA", "CBDA"],
        accentColor: "#007c45",
    },

    CBDVA: {
        slug: "CBDVA",
        name: "Cannabidivarinic Acid",
        abbreviation: "CBDVa",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₁₉H₂₄O₄",
        molecularWeight: "320.38 g/mol",
        boilingPoint: "—",
        cas: "31932-96-4",
        psychoactive: false,
        description: "CBDVa is the acidic precursor to CBDV, biosynthesized in the cannabis plant from divarinic acid. It is found predominantly in hemp and low-THC cannabis cultivars. Like other acid-form cannabinoids, it decarboxylates to its active form upon heating. Research specifically on CBDVa is limited but its conversion product CBDV is being actively investigated for neurological conditions.",
        effects: ["Anti-inflammatory", "Precursor to CBDV"],
        aroma: [],
        foundIn: ["Hemp", "Low-THC cannabis"],
        receptors: { CB1: "None", CB2: "None" },
        researchAreas: ["Inflammation", "Precursor chemistry"],
        relatedCompounds: ["CBDV", "CBD", "THCVa"],
        accentColor: "#007c45",
    },

    CBGa: {
        slug: "CBGa",
        name: "Cannabigerolic Acid",
        abbreviation: "CBGa",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₂₂H₃₂O₄",
        molecularWeight: "360.49 g/mol",
        boilingPoint: "—",
        cas: "25555-57-1",
        psychoactive: false,
        description: "CBGa is often called the 'stem cell' or 'mother cannabinoid' of the cannabis plant. It is the biosynthetic precursor from which all major cannabinoids are derived — specific enzymes convert CBGa into THCa, CBDa, or CBCa depending on the cultivar's genetics. As cannabis plants mature and are exposed to UV light or heat, CBGa levels naturally decline as it is converted to these downstream cannabinoids. CBGa has attracted research interest for its ability to inhibit COX-1 and COX-2 enzymes (anti-inflammatory pathway), and has shown activity against certain bacterial strains.",
        effects: ["Anti-inflammatory", "Antibacterial", "Antiproliferative"],
        aroma: [],
        foundIn: ["Young cannabis plants", "Fiber hemp"],
        receptors: { CB1: "None", CB2: "Low" },
        researchAreas: ["Inflammation", "Antibacterial", "Cancer", "Metabolic syndrome"],
        relatedCompounds: ["CBG", "THCa", "CBDA", "CBCa"],
        accentColor: "#405c6a",
    },

    CBG: {
        slug: "CBG",
        name: "Cannabigerol",
        abbreviation: "CBG",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₂₁H₃₂O₂",
        molecularWeight: "316.48 g/mol",
        boilingPoint: "52°C (126°F)",
        cas: "25654-31-3",
        psychoactive: false,
        description: "CBG is the decarboxylated form of CBGa and is found in very low concentrations in most commercial cannabis cultivars (typically <1%) since it is largely converted to THC, CBD, and CBC during plant maturation. Its low natural abundance has given it the nickname 'the Rolls-Royce of cannabinoids.' Pharmacologically, CBG acts as a partial agonist/antagonist at CB1 and CB2 receptors and potently inhibits the reuptake of GABA. It has demonstrated significant antibacterial activity against MRSA strains and shows promising neuroprotective effects in preclinical models of Huntington's disease.",
        effects: ["Antibacterial", "Neuroprotective", "Anti-inflammatory", "Analgesic", "Mood elevation"],
        aroma: [],
        foundIn: ["High-CBG cannabis cultivars", "Young cannabis plants"],
        receptors: { CB1: "Low", CB2: "Low" },
        researchAreas: ["Glaucoma", "Inflammatory bowel disease", "MRSA", "Neurodegeneration", "Bladder dysfunction"],
        relatedCompounds: ["CBGa", "CBD", "beta_caryophyllene"],
        accentColor: "#405c6a",
    },

    CBCa: {
        slug: "CBCa",
        name: "Cannabichromenic Acid",
        abbreviation: "CBCa",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₂₂H₃₀O₄",
        molecularWeight: "358.47 g/mol",
        boilingPoint: "—",
        cas: "25828-28-0",
        psychoactive: false,
        description: "CBCa is the acidic precursor to cannabichromene (CBC), derived from CBGa through enzymatic cyclization. It is present in cannabis plants in trace amounts and converts to CBC upon decarboxylation. CBCa has demonstrated selective inhibition of TRPA1 (a pain receptor) and may have therapeutic applications for pain and inflammation. Like other acid-form cannabinoids, it is non-psychoactive and its research profile is relatively nascent.",
        effects: ["Anti-inflammatory", "Analgesic", "Precursor to CBC"],
        aroma: [],
        foundIn: ["Cannabis (trace amounts)"],
        receptors: { CB1: "None", CB2: "None" },
        researchAreas: ["Pain", "Inflammation"],
        relatedCompounds: ["CBC", "CBGa", "THCa"],
        accentColor: "#405c6a",
    },

    CBC: {
        slug: "CBC",
        name: "Cannabichromene",
        abbreviation: "CBC",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₂₁H₃₀O₂",
        molecularWeight: "314.46 g/mol",
        boilingPoint: "220°C (428°F)",
        cas: "20675-51-8",
        psychoactive: false,
        description: "CBC is the third most abundant cannabinoid in cannabis after THC and CBD, yet remains largely unknown to the general public. Unlike THC and CBD, CBC does not significantly bind to CB1 or CB2 receptors but exerts its effects primarily through TRPV1 and TRPA1 transient receptor potential channels, as well as by inhibiting the reuptake of the endocannabinoid anandamide. In preclinical studies, CBC has shown antidepressant effects, synergistic analgesic activity alongside THC and CBD, and may stimulate the differentiation of neural stem progenitor cells — a finding of significant interest for neurodegenerative disease research.",
        effects: ["Antidepressant", "Analgesic", "Anti-inflammatory", "Neuroprotective", "Antibacterial"],
        aroma: [],
        foundIn: ["Cannabis flower", "Tropical cannabis cultivars"],
        receptors: { CB1: "None", CB2: "Low" },
        researchAreas: ["Depression", "Pain", "Neurogenesis", "Acne", "Inflammation"],
        relatedCompounds: ["CBCa", "CBD", "delta_9_THC"],
        accentColor: "#405c6a",
    },

    CBN: {
        slug: "CBN",
        name: "Cannabinol",
        abbreviation: "CBN",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₂₁H₂₆O₂",
        molecularWeight: "310.43 g/mol",
        boilingPoint: "185°C (365°F)",
        cas: "521-35-7",
        psychoactive: true,
        description: "CBN is a mildly psychoactive cannabinoid formed from the oxidation and degradation of THC when cannabis is exposed to heat, light, or oxygen over time. It was actually the first cannabinoid ever isolated, in 1896. Often marketed as a sleep aid, CBN's sedative reputation stems largely from animal studies and anecdotal reports — rigorous human clinical evidence for sleep specifically is still limited. What is established is that CBN is a partial CB1 agonist with roughly 25% of THC's potency, and it has shown antimicrobial activity particularly against MRSA strains and demonstrates analgesic properties in preclinical models.",
        effects: ["Mildly sedative", "Analgesic", "Antibacterial", "Anti-inflammatory", "Appetite stimulation"],
        aroma: [],
        foundIn: ["Aged/oxidized cannabis", "Hashish"],
        receptors: { CB1: "Low", CB2: "Moderate" },
        researchAreas: ["Sleep", "Pain", "MRSA", "ALS", "Seizures"],
        relatedCompounds: ["delta_9_THC", "Myrcene", "Linalool"],
        accentColor: "#8b5cf6",
    },

    CBNA: {
        slug: "CBNA",
        name: "Cannabinolic Acid",
        abbreviation: "CBNa",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₂₁H₂₄O₄",
        molecularWeight: "356.41 g/mol",
        boilingPoint: "—",
        cas: "5765-51-5",
        psychoactive: false,
        description: "CBNa is the acidic form of cannabinol, produced through the oxidation of THCa rather than through direct biosynthesis. It is typically present in trace amounts in mature cannabis and increases as plant material ages. CBNa decarboxylates to CBN upon heating. Research directly on CBNa is minimal, and its pharmacological properties are largely inferred from its relationship to CBN. It is primarily a marker of sample age and oxidation state.",
        effects: ["Anti-inflammatory", "Precursor to CBN"],
        aroma: [],
        foundIn: ["Aged cannabis", "Oxidized cannabis products"],
        receptors: { CB1: "None", CB2: "Low" },
        researchAreas: ["Inflammation"],
        relatedCompounds: ["CBN", "THCa", "CBDA"],
        accentColor: "#8b5cf6",
    },

    CBGVA: {
        slug: "CBGVA",
        name: "Cannabigerovarinic Acid",
        abbreviation: "CBGVa",
        type: "cannabinoid",
        subtype: "major",
        formula: "C₁₉H₂₈O₄",
        molecularWeight: "324.42 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: false,
        description: "CBGVa is the acidic precursor to cannabigerovarin (CBGV), itself the propyl homolog of CBG. It acts as a biosynthetic precursor for the propyl cannabinoid series (THCV, CBDV, CBCV) in the same way CBGa serves as precursor to the pentyl series. CBGVa is found predominantly in African cannabis cultivars and those selectively bred for high THCV content. Research on CBGVa specifically is extremely limited; its significance is primarily as a biosynthetic building block.",
        effects: ["Precursor to propyl cannabinoids"],
        aroma: [],
        foundIn: ["African cannabis cultivars"],
        receptors: { CB1: "None", CB2: "Low" },
        researchAreas: ["Precursor chemistry"],
        relatedCompounds: ["CBG", "CBGa", "THCV"],
        accentColor: "#405c6a",
    },

    // ─── MINOR / NOVEL CANNABINOIDS ───────────────────────────────────────────

    CBL: {
        slug: "CBL",
        name: "Cannabicyclol",
        abbreviation: "CBL",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₂₁H₃₀O₂",
        molecularWeight: "314.46 g/mol",
        boilingPoint: "—",
        cas: "16368-78-6",
        psychoactive: false,
        description: "CBL is formed from the photochemical conversion of CBC when cannabis is exposed to ultraviolet light — making it a degradation product rather than a primary biosynthetic compound. It is found in trace amounts in most cannabis samples and increases in products with prolonged UV exposure. CBL is structurally characterized by its unique cyclopropyl ring. Research on its specific pharmacological activities is sparse, and it is generally considered to have minimal psychoactive or therapeutic activity based on available data.",
        effects: ["Minimal known activity"],
        aroma: [],
        foundIn: ["UV-exposed cannabis", "Aged cannabis"],
        receptors: { CB1: "None", CB2: "None" },
        researchAreas: ["Degradation chemistry"],
        relatedCompounds: ["CBC", "CBCa"],
        accentColor: "#405c6a",
    },

    CBE: {
        slug: "CBE",
        name: "Cannabielsoin",
        abbreviation: "CBE",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₂₁H₃₀O₃",
        molecularWeight: "330.46 g/mol",
        boilingPoint: "—",
        cas: "52025-76-0",
        psychoactive: false,
        description: "CBE is a metabolite of CBD formed in the liver and found in trace amounts in cannabis. It arises through epoxidation of CBD's central ring system. It has been detected in the urine and feces of animals and humans who consumed CBD. Research on CBE's direct pharmacological activity is very limited, though it is not believed to be significantly psychoactive. Its primary scientific significance is as a marker of CBD metabolism.",
        effects: ["Minimal known activity", "CBD metabolite"],
        aroma: [],
        foundIn: ["Cannabis (trace)", "CBD metabolite in vivo"],
        receptors: { CB1: "None", CB2: "None" },
        researchAreas: ["Cannabinoid metabolism"],
        relatedCompounds: ["CBD", "CBDA"],
        accentColor: "#405c6a",
    },

    CBT: {
        slug: "CBT",
        name: "Cannabitriol",
        abbreviation: "CBT",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₂₁H₃₂O₃",
        molecularWeight: "332.48 g/mol",
        boilingPoint: "—",
        cas: "7464-97-3",
        psychoactive: false,
        description: "CBT is a minor cannabinoid with nine known isomers, produced through the hydration of the THC double bond. It was first isolated in 1966 from Lebanese hashish. CBT is found in very small concentrations in cannabis and has received little dedicated study. What limited binding data exists suggests it has weak affinity for CB1 and CB2 receptors. Some researchers have noted structural similarities to compounds with vasodilatory effects, but clinical data is absent.",
        effects: ["Mild CB1/CB2 activity"],
        aroma: [],
        foundIn: ["Cannabis (trace)", "Hashish"],
        receptors: { CB1: "Low", CB2: "Low" },
        researchAreas: ["Receptor binding"],
        relatedCompounds: ["delta_9_THC", "CBN"],
        accentColor: "#405c6a",
    },

    CBND: {
        slug: "CBND",
        name: "Cannabinodiol",
        abbreviation: "CBND",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₂₁H₂₆O₂",
        molecularWeight: "310.43 g/mol",
        boilingPoint: "—",
        cas: "20668-36-4",
        psychoactive: false,
        description: "CBND is formed from the oxidative degradation of CBD, similar to how CBN forms from THC. It is found in aged and oxidized cannabis material. CBND is an aromatic compound with a fully conjugated ring system. Like CBN, it can be used as a marker of sample oxidation and age. Research specific to CBND is minimal and its therapeutic potential remains largely unexplored.",
        effects: ["Minimal known activity", "Oxidation marker"],
        aroma: [],
        foundIn: ["Aged cannabis", "Oxidized CBD products"],
        receptors: { CB1: "Low", CB2: "Low" },
        researchAreas: ["Degradation markers"],
        relatedCompounds: ["CBD", "CBN"],
        accentColor: "#405c6a",
    },

    CBV: {
        slug: "CBV",
        name: "Cannabivarin",
        abbreviation: "CBV",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₁₉H₂₂O₂",
        molecularWeight: "282.38 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: false,
        description: "CBV is the propyl analog of CBN, formed through the oxidation and aromatization of THCV — mirroring CBN's relationship to THC. It is found in aged cannabis that originally contained significant THCV, particularly some African cultivars. CBV is one of the least-studied cannabinoids in the panel; its pharmacological properties have not been characterized in any major published studies. Its presence on a COA primarily indicates the sample contained THCV that has since oxidized.",
        effects: ["Minimal known activity"],
        aroma: [],
        foundIn: ["Aged high-THCV cannabis"],
        receptors: { CB1: "None", CB2: "None" },
        researchAreas: ["Degradation chemistry"],
        relatedCompounds: ["THCV", "CBN"],
        accentColor: "#405c6a",
    },

    THCP: {
        slug: "THCP",
        name: "Tetrahydrocannabiphorol",
        abbreviation: "THCp",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₂₃H₃₄O₂",
        molecularWeight: "342.52 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: true,
        description: "THCp was identified in 2019 by a team of Italian researchers who discovered it while analyzing an unusual Italian medical cannabis variety. It is a homolog of D9-THC with a 7-carbon alkyl chain instead of 5, and this seemingly small structural difference results in approximately 33× greater binding affinity for CB1 receptors compared to D9-THC — making it the most potent naturally-occurring cannabinoid receptor agonist ever identified. It occurs in cannabis only in trace amounts (the sample that led to its discovery contained roughly 0.0023%). Despite its extreme potency in binding assays, its real-world psychoactive effects at the concentrations found in cannabis are an area of ongoing research.",
        effects: ["Potent psychoactive", "Analgesic", "Sedative"],
        aroma: [],
        foundIn: ["Cannabis (trace amounts)"],
        receptors: { CB1: "Very High", CB2: "High" },
        researchAreas: ["Receptor pharmacology", "Pain", "Sleep"],
        relatedCompounds: ["delta_9_THC", "THCH", "THC_B"],
        accentColor: "#e11d48",
    },

    CBDP: {
        slug: "CBDP",
        name: "Cannabidiphorol",
        abbreviation: "CBDp",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₂₃H₃₄O₂",
        molecularWeight: "342.52 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: false,
        description: "CBDp was discovered alongside THCp in the same 2019 Italian study. It is the 7-carbon chain homolog of CBD. Unlike THCp, CBDp's extended alkyl chain does not produce dramatically enhanced receptor binding — consistent with CBD's general pattern of low CB1/CB2 affinity regardless of chain length. CBDp has not been found to be psychoactive and research on its specific therapeutic applications is at very early stages. Its role in the entourage effect is not yet understood.",
        effects: ["Non-psychoactive", "Research compound"],
        aroma: [],
        foundIn: ["Cannabis (trace amounts)"],
        receptors: { CB1: "None", CB2: "Low" },
        researchAreas: ["Receptor pharmacology"],
        relatedCompounds: ["CBD", "THCP"],
        accentColor: "#405c6a",
    },

    CBDH: {
        slug: "CBDH",
        name: "Cannabidihydro",
        abbreviation: "CBDH",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₂₁H₃₂O₂",
        molecularWeight: "316.48 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: false,
        description: "CBDH (also called 8,9-dihydrocannabidiol) is a reduced form of CBD in which the double bond in the terpene portion of the molecule is hydrogenated. It occurs as a minor constituent in some cannabis extracts and has been identified as a minor metabolite. Clinical data on CBDH is very sparse; it is not considered psychoactive and its pharmacological activity relative to CBD has not been established in published literature.",
        effects: ["Non-psychoactive", "Research compound"],
        aroma: [],
        foundIn: ["Cannabis extracts (trace)"],
        receptors: { CB1: "None", CB2: "Low" },
        researchAreas: ["Cannabinoid chemistry"],
        relatedCompounds: ["CBD", "HHC"],
        accentColor: "#405c6a",
    },

    THCH: {
        slug: "THCH",
        name: "Tetrahydrocannabihexol",
        abbreviation: "THCH",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₂₂H₃₂O₂",
        molecularWeight: "328.49 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: true,
        description: "THCH is the 6-carbon hexyl chain homolog of THC, identified by the same Italian research group that discovered THCp and CBDp. Its receptor binding affinity at CB1 is significantly higher than D9-THC but lower than THCp. In the same 2020 discovery paper, it was noted to produce significant analgesic and motor impairment effects in rodent models at lower doses than D9-THC. Like THCp, it exists in cannabis only in trace quantities, but may contribute to the overall potency profile of high-THC cultivars.",
        effects: ["Psychoactive", "Analgesic", "Motor impairment"],
        aroma: [],
        foundIn: ["Cannabis (trace amounts)"],
        receptors: { CB1: "High", CB2: "Moderate" },
        researchAreas: ["Pain", "Receptor pharmacology"],
        relatedCompounds: ["THCP", "delta_9_THC", "THC_B"],
        accentColor: "#e11d48",
    },

    Delta_10_THC: {
        slug: "Delta_10_THC",
        name: "Delta-10 Tetrahydrocannabinol",
        abbreviation: "Δ10-THC",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₂₁H₃₀O₂",
        molecularWeight: "314.46 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: true,
        description: "Delta-10 THC is an isomer of D9-THC with a double bond at the 10th carbon position. It occurs only in trace amounts naturally in cannabis and virtually all commercial Delta-10 THC is produced semi-synthetically through isomerization of CBD or D9-THC. Users describe its effects as more energizing and sativa-like than D9-THC, with less associated anxiety. Its CB1 binding affinity is lower than D9-THC. Delta-10 THC gained commercial popularity following the 2018 Farm Bill's hemp legalization, but regulatory status remains in flux across various jurisdictions.",
        effects: ["Mild euphoria", "Energizing", "Focus", "Less anxiety than D9"],
        aroma: [],
        foundIn: ["Cannabis (trace)", "Semi-synthetic from CBD"],
        receptors: { CB1: "Low", CB2: "Low" },
        researchAreas: ["Psychoactive cannabinoid pharmacology"],
        relatedCompounds: ["delta_9_THC", "D8_THC", "HHC"],
        accentColor: "#405c6a",
    },

    THC_O: {
        slug: "THC_O",
        name: "THC-O Acetate",
        abbreviation: "THC-O",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₂₃H₃₂O₃",
        molecularWeight: "356.50 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: true,
        description: "THC-O acetate is a fully synthetic cannabinoid — it does not occur naturally in cannabis. It is produced by acetylating D9-THC or D8-THC using acetic anhydride, a process that requires specialized chemical equipment and carries significant safety risks. It acts as a prodrug: after ingestion, the acetate ester is cleaved by metabolic enzymes to release the active THC. The acetate group increases lipophilicity and is reported to produce effects 2–3× more potent than D9-THC, with delayed onset. Concerns have been raised about pulmonary toxicity when vaped, specifically related to ketene formation. Its legal status in the US is actively contested.",
        effects: ["Potent psychoactive", "Delayed onset", "Strong euphoria", "Potential pulmonary risk (vaporized)"],
        aroma: [],
        foundIn: ["Synthetic only — not found in cannabis plants"],
        receptors: { CB1: "High", CB2: "Low" },
        researchAreas: ["Prodrug pharmacokinetics", "Safety"],
        relatedCompounds: ["delta_9_THC", "D8_THC", "HHC"],
        accentColor: "#dc2626",
    },

    HHC: {
        slug: "HHC",
        name: "Hexahydrocannabinol",
        abbreviation: "HHC",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₂₁H₃₂O₂",
        molecularWeight: "316.48 g/mol",
        boilingPoint: "—",
        cas: "6692-85-9",
        psychoactive: true,
        description: "HHC is the fully hydrogenated analog of THC, produced by adding hydrogen atoms across the central double bond of D9-THC (hydrogenation). Trace amounts occur naturally in cannabis pollen, but commercial HHC is produced semi-synthetically. HHC is notably more stable than THC — the hydrogenated structure resists UV degradation and oxidation, giving it a longer shelf life. It binds to CB1 receptors with moderate affinity and produces psychoactive effects broadly similar to THC, though users often report a slightly different character. HHC comes as a mixture of two diastereomers; the 9R isomer is significantly more potent than the 9S form.",
        effects: ["Psychoactive", "Euphoria", "Analgesic", "UV-stable"],
        aroma: [],
        foundIn: ["Cannabis pollen (trace)", "Semi-synthetic from THC"],
        receptors: { CB1: "Moderate", CB2: "Low" },
        researchAreas: ["Receptor pharmacology", "Stability chemistry"],
        relatedCompounds: ["delta_9_THC", "D8_THC", "Delta_10_THC"],
        accentColor: "#405c6a",
    },

    THC_B: {
        slug: "THC_B",
        name: "Tetrahydrocannabutol",
        abbreviation: "THC-B",
        type: "cannabinoid",
        subtype: "minor",
        formula: "C₂₀H₂₈O₂",
        molecularWeight: "300.44 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: true,
        description: "THC-B is the 4-carbon butyl chain homolog of D9-THC, co-discovered with THCp and THCH in the 2020 Italian study. Despite having a shorter side chain than D9-THC, it was found to have stronger CB1 receptor binding affinity than D9-THC in initial assay data. This was a counterintuitive finding that has generated interest in understanding the structural activity relationship of alkyl chain length in cannabinoids more thoroughly. THC-B is present only in trace quantities in cannabis and its in vivo pharmacology remains mostly uncharacterized.",
        effects: ["Psychoactive", "Analgesic"],
        aroma: [],
        foundIn: ["Cannabis (trace amounts)"],
        receptors: { CB1: "High", CB2: "Moderate" },
        researchAreas: ["Structure-activity relationships", "Receptor pharmacology"],
        relatedCompounds: ["delta_9_THC", "THCP", "THCH"],
        accentColor: "#405c6a",
    },

    // ─── TERPENES ─────────────────────────────────────────────────────────────

    Myrcene: {
        slug: "Myrcene",
        name: "Myrcene",
        abbreviation: "Myrcene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆",
        molecularWeight: "136.23 g/mol",
        boilingPoint: "167°C (333°F)",
        cas: "123-35-3",
        psychoactive: false,
        description: "Myrcene is the most abundant terpene in modern cannabis cultivars, often comprising over 50% of a strain's total terpene content. Its earthy, musky, herbal aroma is characteristic of classic indica varieties. Myrcene is believed to enhance cannabinoid uptake across the blood-brain barrier by increasing cellular membrane permeability, which may explain the folk wisdom that eating a ripe mango (which contains high myrcene) before consuming cannabis intensifies its effects. In preclinical studies, myrcene has demonstrated muscle-relaxant, sedative, and analgesic properties, as well as significant anti-inflammatory activity through inhibition of cytokines. It is also found in high concentrations in hops (Humulus lupulus), the plant most closely related to cannabis.",
        effects: ["Sedative", "Muscle relaxant", "Analgesic", "Anti-inflammatory", "Anxiolytic"],
        aroma: ["Earthy", "Musky", "Herbal", "Clove-like"],
        foundIn: ["Mangoes", "Hops", "Lemongrass", "Bay leaves", "Thyme", "Cannabis"],
        receptors: null,
        researchAreas: ["Pain", "Inflammation", "Sleep", "Muscle relaxation"],
        relatedCompounds: ["delta_9_THC", "beta_caryophyllene", "Linalool", "CBN"],
        accentColor: "#405c6a",
    },

    beta_caryophyllene: {
        slug: "beta_caryophyllene",
        name: "Beta-Caryophyllene",
        abbreviation: "BCP",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "130°C (266°F)",
        cas: "87-44-5",
        psychoactive: false,
        description: "Beta-caryophyllene holds a unique position in the plant kingdom as the only known terpene that directly binds to and activates cannabinoid receptors — specifically the CB2 receptor. This makes it technically both a terpene and a dietary cannabinoid, leading the FDA to classify it as a Generally Recognized As Safe (GRAS) food additive found in spices like black pepper and cloves. Its CB2 activation produces significant anti-inflammatory and analgesic effects through immune system modulation rather than central nervous system effects, making it a compelling candidate for pain and inflammatory conditions without psychoactivity. It is found at high concentrations in many cannabis cultivars and contributes significantly to the anti-inflammatory character of the entourage effect.",
        effects: ["Anti-inflammatory", "Analgesic", "Anxiolytic", "Antidepressant", "Gastroprotective"],
        aroma: ["Spicy", "Peppery", "Woody", "Clove-like"],
        foundIn: ["Black pepper", "Cloves", "Basil", "Oregano", "Rosemary", "Hops"],
        receptors: null,
        researchAreas: ["Inflammation", "Pain", "Anxiety", "Metabolic disorders", "Addiction"],
        relatedCompounds: ["caryophyllene_oxide", "Myrcene", "CBD", "CBG"],
        accentColor: "#405c6a",
    },

    Limonene: {
        slug: "Limonene",
        name: "Limonene",
        abbreviation: "Limonene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆",
        molecularWeight: "136.23 g/mol",
        boilingPoint: "176°C (349°F)",
        cas: "5989-27-5",
        psychoactive: false,
        description: "Limonene is the second most widely distributed terpene in nature after pinene, and is immediately recognizable by its bright citrus aroma. In cannabis, it is associated with uplifting, energizing, and mood-enhancing effects — qualities attributed to sativa-dominant strains. Limonene is a potent anxiolytic in preclinical models, acting through adenosine A2A receptors and 5-HT1A serotonin receptors. It readily crosses the blood-brain barrier and has been shown to enhance the absorption of other terpenes and cannabinoids. Commercial limonene is used extensively in food flavoring, cleaning products, and cosmetics due to its GRAS status.",
        effects: ["Anxiolytic", "Antidepressant", "Antifungal", "Immune stimulation", "Anti-reflux"],
        aroma: ["Citrus", "Lemon", "Orange", "Lime"],
        foundIn: ["Lemon rind", "Orange peel", "Grapefruit", "Lime", "Rosemary", "Juniper"],
        receptors: null,
        researchAreas: ["Anxiety", "Depression", "Cancer prevention", "Inflammation"],
        relatedCompounds: ["alpha_pinene", "Linalool", "CBD"],
        accentColor: "#ca8a04",
    },

    alpha_pinene: {
        slug: "alpha_pinene",
        name: "Alpha-Pinene",
        abbreviation: "α-Pinene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆",
        molecularWeight: "136.23 g/mol",
        boilingPoint: "155°C (311°F)",
        cas: "80-56-8",
        psychoactive: false,
        description: "Alpha-pinene is the most abundant terpene in the natural world and the predominant constituent of pine resin. In cannabis, it is associated with alertness, mental clarity, and focus — widely considered to counteract short-term memory impairment associated with THC through its activity as an acetylcholinesterase inhibitor. Alpha-pinene is a potent bronchodilator at very low atmospheric concentrations (as low as 0.1 ppm), which may partly explain the expanded lung capacity some users experience. It also demonstrates significant anti-inflammatory properties via NF-κB pathway inhibition and has shown antibacterial activity against a range of pathogens.",
        effects: ["Memory retention", "Alertness", "Anti-inflammatory", "Bronchodilator", "Antibacterial"],
        aroma: ["Pine", "Fresh", "Woody", "Turpentine-like"],
        foundIn: ["Pine trees", "Rosemary", "Sage", "Eucalyptus", "Frankincense"],
        receptors: null,
        researchAreas: ["Memory", "Asthma", "Inflammation", "Pain"],
        relatedCompounds: ["beta_pinene", "Limonene", "Camphene", "delta_9_THC"],
        accentColor: "#166534",
    },

    beta_pinene: {
        slug: "beta_pinene",
        name: "Beta-Pinene",
        abbreviation: "β-Pinene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆",
        molecularWeight: "136.23 g/mol",
        boilingPoint: "166°C (331°F)",
        cas: "127-91-3",
        psychoactive: false,
        description: "Beta-pinene is a structural isomer of alpha-pinene, differing in the position of the double bond. Though it shares the characteristic pine aroma of its isomer, it lends a slightly more herbal, woody quality. In the plant kingdom, beta-pinene serves as a precursor to many other important terpenes. Its biological activities include antibacterial effects, bronchodilation comparable to alpha-pinene, and anxiolytic properties in preclinical models. It is often found alongside alpha-pinene in cannabis terpene profiles.",
        effects: ["Antibacterial", "Bronchodilator", "Anti-inflammatory", "Anxiolytic"],
        aroma: ["Pine", "Herbal", "Woody", "Fresh"],
        foundIn: ["Pine trees", "Hops", "Parsley", "Basil", "Dill"],
        receptors: null,
        researchAreas: ["Respiratory health", "Antibacterial", "Anxiety"],
        relatedCompounds: ["alpha_pinene", "Camphene", "Limonene"],
        accentColor: "#166534",
    },

    Linalool: {
        slug: "Linalool",
        name: "Linalool",
        abbreviation: "Linalool",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₈O",
        molecularWeight: "154.25 g/mol",
        boilingPoint: "198°C (388°F)",
        cas: "78-70-6",
        psychoactive: false,
        description: "Linalool is the primary aromatic constituent of lavender and is one of the most extensively studied terpenes for its psychoactive-adjacent properties. In preclinical models, linalool has demonstrated strong sedative, anxiolytic, and analgesic effects through modulation of GABA-A receptors — a mechanism shared with benzodiazepine drugs, but without the addiction potential. It also exhibits notable anti-inflammatory effects through reduction of white blood cell infiltration and inflammatory markers. In cannabis, strains with high linalool content are generally associated with a calming, sedating experience, and linalool is considered one of the key contributors to the well-documented relaxant qualities of lavender aromatherapy.",
        effects: ["Anxiolytic", "Sedative", "Analgesic", "Anti-inflammatory", "Anticonvulsant"],
        aroma: ["Floral", "Lavender", "Spicy", "Citrus-woody"],
        foundIn: ["Lavender", "Coriander", "Rosewood", "Birch", "Basil"],
        receptors: null,
        researchAreas: ["Anxiety", "Sleep", "Pain", "Epilepsy", "Alzheimer's disease"],
        relatedCompounds: ["Myrcene", "beta_caryophyllene", "CBD", "CBN"],
        accentColor: "#7c3aed",
    },

    Terpinolene: {
        slug: "Terpinolene",
        name: "Terpinolene",
        abbreviation: "Terpinolene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆",
        molecularWeight: "136.23 g/mol",
        boilingPoint: "185°C (365°F)",
        cas: "586-62-9",
        psychoactive: false,
        description: "Terpinolene is a multifaceted terpene with a complex, multi-layered aroma that ranges from fresh and pine-like to floral and herbaceous. It is the dominant terpene in several classic sativa-leaning cultivar families (particularly Jack Herer lineage strains) and is associated with energizing, uplifting, and slightly psychedelic-adjacent character. Despite this association with stimulating effects, preclinical studies on terpinolene demonstrate sedative properties and downregulation of central nervous system activity — highlighting the complexity of terpene-behavior relationships. It also shows antioxidant activity and antibacterial/antifungal effects.",
        effects: ["Antioxidant", "Sedative (isolated)", "Antibacterial", "Antifungal", "Uplifting (anecdotal)"],
        aroma: ["Fresh", "Floral", "Herbal", "Pine", "Citrus"],
        foundIn: ["Lilac", "Nutmeg", "Apple", "Tea tree", "Cumin"],
        receptors: null,
        researchAreas: ["Cancer prevention", "Cardiovascular health", "Antibacterial"],
        relatedCompounds: ["alpha_pinene", "Limonene", "Myrcene"],
        accentColor: "#0369a1",
    },

    alpha_humulene: {
        slug: "alpha_humulene",
        name: "Humulene",
        abbreviation: "Humulene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "106°C (223°F)",
        cas: "6753-98-6",
        psychoactive: false,
        description: "Humulene (alpha-humulene) is a sesquiterpene that shares its origin with beta-caryophyllene — both are derived from farnesyl pyrophosphate and often found together in cannabis profiles. It is the terpene primarily responsible for the earthy, woody, spicy aroma of hops, and contributes significantly to the characteristic smell of cannabis. Humulene has been studied as an appetite suppressant (notable in a cannabinoid system otherwise known for stimulating appetite) and demonstrates anti-inflammatory effects comparable to dexamethasone in some preclinical models when administered by inhalation. It also shows antitumor activity in preclinical settings.",
        effects: ["Appetite suppressant", "Anti-inflammatory", "Antibacterial", "Antitumor"],
        aroma: ["Earthy", "Woody", "Spicy", "Herbal"],
        foundIn: ["Hops", "Sage", "Ginseng", "Clove", "Black pepper"],
        receptors: null,
        researchAreas: ["Inflammation", "Appetite regulation", "Cancer", "Antibacterial"],
        relatedCompounds: ["beta_caryophyllene", "Myrcene", "Bisabolol"],
        accentColor: "#78350f",
    },

    Ocimene: {
        slug: "Ocimene",
        name: "Ocimene",
        abbreviation: "Ocimene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆",
        molecularWeight: "136.23 g/mol",
        boilingPoint: "~50°C (122°F) at low pressure",
        cas: "13877-91-3",
        psychoactive: false,
        description: "Ocimene is a monoterpene with a sweet, herbal, woody aroma that often adds a pleasant complexity to cannabis terpene profiles. It exists in multiple isomeric forms (cis-beta, trans-beta, and alpha) with slightly different aromatic characteristics. In plants, ocimene serves as a natural defense compound against insects and pathogens. Pharmacologically, it has demonstrated antifungal and antiviral activity in various studies, and some research suggests decongestant properties relevant to respiratory applications. It is found in many aromatic herbs including basil, mint, and parsley.",
        effects: ["Antifungal", "Antiviral", "Decongestant", "Anti-inflammatory"],
        aroma: ["Sweet", "Herbal", "Woody", "Citrus-floral"],
        foundIn: ["Mint", "Parsley", "Basil", "Tarragon", "Mango"],
        receptors: null,
        researchAreas: ["Antifungal applications", "Respiratory health", "Antiviral"],
        relatedCompounds: ["Myrcene", "Terpinolene", "Limonene"],
        accentColor: "#15803d",
    },

    caryophyllene_oxide: {
        slug: "caryophyllene_oxide",
        name: "Caryophyllene Oxide",
        abbreviation: "Caryo. Oxide",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄O",
        molecularWeight: "220.35 g/mol",
        boilingPoint: "~130°C",
        cas: "1139-30-6",
        psychoactive: false,
        description: "Caryophyllene oxide is the oxidized form of beta-caryophyllene, formed when beta-caryophyllene is exposed to oxygen over time. It is notably one of the compounds most readily detected by drug-sniffing dogs trained to identify cannabis, making its presence on a COA of particular interest in law enforcement contexts. Unlike its precursor beta-caryophyllene, caryophyllene oxide has not been shown to activate CB2 receptors. However, it demonstrates antifungal activity, has shown insecticidal properties in research, and may contribute to the anti-inflammatory character of cannabis products containing it.",
        effects: ["Antifungal", "Insecticidal", "Anti-inflammatory", "Oxidation marker"],
        aroma: ["Spicy", "Dry", "Woody", "Cedar"],
        foundIn: ["Cloves", "Basil", "Oregano", "Ylang-ylang", "Lemon balm"],
        receptors: null,
        researchAreas: ["Antifungal applications", "Insecticide", "Inflammation"],
        relatedCompounds: ["beta_caryophyllene", "Nerolidol", "Bisabolol"],
        accentColor: "#78350f",
    },

    Nerolidol: {
        slug: "Nerolidol",
        name: "Nerolidol",
        abbreviation: "Nerolidol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₆O",
        molecularWeight: "222.37 g/mol",
        boilingPoint: "122°C (252°F)",
        cas: "7212-44-4",
        psychoactive: false,
        description: "Nerolidol is a sesquiterpene alcohol with a complex, floral, woody, citrus aroma reminiscent of jasmine and tea tree. It exists as two stereoisomers (cis and trans) with slightly different properties. Nerolidol has attracted significant research interest for its antiparasitic activity, particularly against malaria and Leishmania. It demonstrates sedative properties in animal studies and has shown potential for skin penetration enhancement — making it relevant as a delivery vehicle for topical cannabinoid formulations. It is also studied for antifungal and antibacterial applications.",
        effects: ["Sedative", "Antiparasitic", "Antifungal", "Skin penetration enhancer"],
        aroma: ["Floral", "Woody", "Citrus", "Jasmine"],
        foundIn: ["Jasmine", "Tea tree", "Lavender", "Ginger", "Niaouli"],
        receptors: null,
        researchAreas: ["Antiparasitic", "Sleep", "Skin delivery", "Antifungal"],
        relatedCompounds: ["Linalool", "Bisabolol", "Geraniol"],
        accentColor: "#7c3aed",
    },

    Bisabolol: {
        slug: "Bisabolol",
        name: "Bisabolol",
        abbreviation: "Bisabolol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₆O",
        molecularWeight: "222.37 g/mol",
        boilingPoint: "153°C (307°F)",
        cas: "515-69-5",
        psychoactive: false,
        description: "Alpha-bisabolol is most famously associated with chamomile (Matricaria chamomilla), where it is the principal active compound responsible for the flower's skin-soothing properties. In cosmetics and dermatology, bisabolol is widely used for its ability to reduce inflammation, accelerate wound healing, and enhance skin penetration of active ingredients. In cannabis, it contributes a subtle floral, sweet, slightly nutty note to the aroma. Pharmacologically, bisabolol has shown analgesic effects via TRPA1 channel modulation, as well as antitumor activity in preclinical cancer models through apoptosis induction.",
        effects: ["Anti-inflammatory", "Analgesic", "Skin-healing", "Antifungal", "Antitumor"],
        aroma: ["Floral", "Sweet", "Nutty", "Lightly citrus"],
        foundIn: ["Chamomile", "Candeia tree", "Cannabis", "Myoporum crassifolium"],
        receptors: null,
        researchAreas: ["Skin disorders", "Pain", "Cancer", "Wound healing"],
        relatedCompounds: ["Nerolidol", "alpha_humulene", "caryophyllene_oxide"],
        accentColor: "#be185d",
    },

    terpineol: {
        slug: "terpineol",
        name: "Terpineol",
        abbreviation: "Terpineol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₈O",
        molecularWeight: "154.25 g/mol",
        boilingPoint: "217°C (423°F)",
        cas: "98-55-5",
        psychoactive: false,
        description: "Alpha-terpineol is a monocyclic monoterpene alcohol with a lilac-like, floral aroma that is widely used in perfumery and cosmetics. In cannabis, it contributes to the characteristic pleasant, slightly floral scent of many indica and hybrid cultivars. Terpineol has demonstrated sedative properties in various animal studies, often synergizing with myrcene to contribute to couch-lock effects in high-myrcene strains. It also shows antibacterial activity against several clinically relevant pathogens and has demonstrated antioxidant properties.",
        effects: ["Sedative", "Antibacterial", "Antioxidant", "Anxiolytic"],
        aroma: ["Floral", "Lilac", "Pine", "Slightly citrus"],
        foundIn: ["Pine trees", "Eucalyptus", "Lime blossoms", "Petitgrain"],
        receptors: null,
        researchAreas: ["Sleep", "Antibacterial", "Antioxidant"],
        relatedCompounds: ["Linalool", "Myrcene", "alpha_pinene"],
        accentColor: "#7c3aed",
    },

    Geraniol: {
        slug: "Geraniol",
        name: "Geraniol",
        abbreviation: "Geraniol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₈O",
        molecularWeight: "154.25 g/mol",
        boilingPoint: "230°C (446°F)",
        cas: "106-24-1",
        psychoactive: false,
        description: "Geraniol is a monoterpenoid alcohol with a soft, sweet, rose-like aroma. It is a primary constituent of geranium, rose, and citronella essential oils. In cannabis, geraniol is typically present in small amounts but contributes a distinctive floral sweetness to the terpene profile. Pharmacologically, geraniol has shown significant neuroprotective properties in multiple preclinical models, reducing neuronal apoptosis and oxidative stress. It is also a potent natural insect repellent and has demonstrated antifungal and antibacterial properties. Some research suggests it may inhibit certain cancer cell lines.",
        effects: ["Neuroprotective", "Insect repellent", "Antifungal", "Antibacterial", "Antitumor"],
        aroma: ["Floral", "Rose", "Fruity", "Sweet"],
        foundIn: ["Rose", "Citronella", "Lemon", "Geranium", "Palmarosa"],
        receptors: null,
        researchAreas: ["Neuroprotection", "Cancer", "Antibacterial", "Repellent"],
        relatedCompounds: ["Linalool", "Nerol", "terpineol"],
        accentColor: "#be185d",
    },

    Camphene: {
        slug: "Camphene",
        name: "Camphene",
        abbreviation: "Camphene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆",
        molecularWeight: "136.23 g/mol",
        boilingPoint: "159°C (318°F)",
        cas: "79-92-5",
        psychoactive: false,
        description: "Camphene has a pungent, earthy, and somewhat damp aroma reminiscent of fir needles and wet soil. In cannabis terpene profiles, camphene is usually a minor component but can contribute to the characteristic 'piney' or 'damp forest' notes of certain strains. Pharmacologically, camphene is of interest for cardiovascular research: a 2011 study found it may lower plasma cholesterol and triglycerides comparably to a pharmaceutical statin, and with fewer reported side effects in the animal model studied. It also shows antioxidant activity in vitro.",
        effects: ["Antioxidant", "Cardiovascular support", "Antibacterial"],
        aroma: ["Earthy", "Pungent", "Damp wood", "Fir needles"],
        foundIn: ["Camphor tree", "Ginger", "Valerian", "Cypress", "Bergamot"],
        receptors: null,
        researchAreas: ["Cardiovascular health", "Cholesterol", "Antioxidant"],
        relatedCompounds: ["alpha_pinene", "beta_pinene", "Borneol"],
        accentColor: "#166534",
    },

    alpha_terpinene: {
        slug: "alpha_terpinene",
        name: "Alpha-Terpinene",
        abbreviation: "α-Terpinene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆",
        molecularWeight: "136.23 g/mol",
        boilingPoint: "174°C (345°F)",
        cas: "99-86-5",
        psychoactive: false,
        description: "Alpha-terpinene is a cyclic monoterpene with a fresh, lemon-like, woody aroma. It is found as a minor constituent in many essential oils including marjoram, tea tree, and cardamom. In cannabis, it contributes to citrusy and herbaceous notes. Alpha-terpinene is one of the terpenes noted for its strong antioxidant activity — particularly through its ability to quench singlet oxygen, a reactive oxygen species. It also has documented antimicrobial properties. One consideration is its tendency to oxidize under air exposure, forming other terpenes including p-cymene.",
        effects: ["Antioxidant", "Antimicrobial", "Anti-inflammatory"],
        aroma: ["Lemon", "Herbal", "Woody", "Fresh"],
        foundIn: ["Marjoram", "Cardamom", "Tea tree", "Lime"],
        receptors: null,
        researchAreas: ["Antioxidant", "Antimicrobial"],
        relatedCompounds: ["gamma_terpinene", "Terpinolene", "Limonene"],
        accentColor: "#ca8a04",
    },

    gamma_terpinene: {
        slug: "gamma_terpinene",
        name: "Gamma-Terpinene",
        abbreviation: "γ-Terpinene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆",
        molecularWeight: "136.23 g/mol",
        boilingPoint: "183°C (361°F)",
        cas: "99-85-4",
        psychoactive: false,
        description: "Gamma-terpinene is a positional isomer of alpha-terpinene with a citrusy, piney aroma. It is a significant component of coriander, tea tree, and ajowan essential oils. In food industry applications, gamma-terpinene serves as a fragrance and flavoring agent. Pharmacologically, it demonstrates antioxidant activity through hydrogen atom transfer mechanisms and has shown antibacterial and antifungal effects. In cannabis it is a minor terpene present in small quantities, often contributing background herbal complexity.",
        effects: ["Antioxidant", "Antibacterial", "Antifungal"],
        aroma: ["Citrus", "Piney", "Herbal"],
        foundIn: ["Coriander", "Tea tree", "Ajowan", "Thyme"],
        receptors: null,
        researchAreas: ["Antioxidant", "Antimicrobial"],
        relatedCompounds: ["alpha_terpinene", "Terpinolene", "Limonene"],
        accentColor: "#ca8a04",
    },

    Nerol: {
        slug: "Nerol",
        name: "Nerol",
        abbreviation: "Nerol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₈O",
        molecularWeight: "154.25 g/mol",
        boilingPoint: "225°C (437°F)",
        cas: "106-25-2",
        psychoactive: false,
        description: "Nerol is the cis isomer of geraniol, sharing the same molecular formula but with a different geometric configuration around the double bond. This structural difference gives nerol a lighter, fresher, more citrusy rose aroma compared to geraniol's heavier, sweeter character. Nerol is found in lemon grass, hops, and many floral essential oils. Its pharmacological properties closely parallel geraniol's, including antifungal, antibacterial, and potential neuroprotective effects. In cannabis terpene profiles, nerol is typically found in small amounts where it subtly enhances floral and citrus notes.",
        effects: ["Antifungal", "Antibacterial", "Neuroprotective"],
        aroma: ["Citrus", "Floral", "Rose", "Fresh"],
        foundIn: ["Lemongrass", "Hops", "Ginger", "Petitgrain"],
        receptors: null,
        researchAreas: ["Antifungal", "Neuroprotection"],
        relatedCompounds: ["Geraniol", "Linalool", "terpineol"],
        accentColor: "#be185d",
    },

    fenchone: {
        slug: "fenchone",
        name: "Fenchone",
        abbreviation: "Fenchone",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆O",
        molecularWeight: "152.23 g/mol",
        boilingPoint: "193°C (379°F)",
        cas: "1195-79-5",
        psychoactive: false,
        description: "Fenchone is a bicyclic monoterpene ketone with a camphor-like, minty aroma. It is the principal active compound in fennel essential oil and is found in significant amounts in absinthe (from wormwood). In cannabis, fenchone is typically present only in trace amounts and is uncommon in most commercial cultivars — its presence often indicates unique genetic heritage or cross-breeding with unusual varietals. Pharmacologically, fenchone has demonstrated antimicrobial, antifungal, and antispasmodic properties. Some research has explored its potential as an acetylcholinesterase inhibitor relevant to Alzheimer's research.",
        effects: ["Antimicrobial", "Antifungal", "Antispasmodic", "Acetylcholinesterase inhibition"],
        aroma: ["Camphor", "Minty", "Earthy", "Fennel-like"],
        foundIn: ["Fennel", "Thuja", "Wormwood", "Absinthe"],
        receptors: null,
        researchAreas: ["Antimicrobial", "Alzheimer's disease", "Antispasmodic"],
        relatedCompounds: ["Camphene", "Borneol", "Fenchol"],
        accentColor: "#0369a1",
    },

    Eucalyptol: {
        slug: "Eucalyptol",
        name: "Eucalyptol",
        abbreviation: "1,8-Cineole",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₈O",
        molecularWeight: "154.25 g/mol",
        boilingPoint: "176°C (349°F)",
        cas: "470-82-6",
        psychoactive: false,
        description: "Eucalyptol (also known as 1,8-cineole) is the primary active compound in eucalyptus essential oil, responsible for its characteristic cool, minty, medicinal aroma. It has one of the most robust clinical profiles of any terpene: eucalyptol is used in pharmaceutical preparations (including Soledum and various cough medicines) and has demonstrated significant anti-inflammatory, bronchodilatory, and mucolytic effects in human clinical trials. In cannabis, eucalyptol is uncommon but when present contributes a distinctive medicinal, cooling quality. It has also shown potential benefits in Alzheimer's disease research through inhibition of acetylcholinesterase.",
        effects: ["Bronchodilator", "Anti-inflammatory", "Antibacterial", "Analgesic", "Mucolytic"],
        aroma: ["Minty", "Cooling", "Medicinal", "Camphor-like"],
        foundIn: ["Eucalyptus", "Tea tree", "Rosemary", "Bay leaves", "Sage"],
        receptors: null,
        researchAreas: ["Respiratory disease", "Asthma", "Alzheimer's disease", "Antibacterial"],
        relatedCompounds: ["Camphene", "alpha_pinene", "terpineol"],
        accentColor: "#0369a1",
    },

    Valencene: {
        slug: "Valencene",
        name: "Valencene",
        abbreviation: "Valencene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "~123°C",
        cas: "4630-07-3",
        psychoactive: false,
        description: "Valencene takes its name from Valencia oranges where it was first identified as a major aromatic constituent. It is a sesquiterpene with a fresh, sweet, citrusy aroma and is a common ingredient in commercial perfumery and food flavoring. In cannabis, valencene contributes a pleasant orange-citrus note and is found at higher concentrations in certain hybrid cultivars. It has shown anti-inflammatory activity in preclinical studies and has been studied as an insect repellent, with effectiveness against Aedes mosquitoes comparable to some commercial repellents.",
        effects: ["Anti-inflammatory", "Insect repellent", "Antifungal"],
        aroma: ["Orange", "Citrus", "Sweet", "Fresh"],
        foundIn: ["Valencia oranges", "Grapefruit", "Tangerines", "Nootka tree"],
        receptors: null,
        researchAreas: ["Insect repellent", "Anti-inflammatory"],
        relatedCompounds: ["Limonene", "Nerolidol", "Bisabolol"],
        accentColor: "#ea580c",
    },

    Borneol: {
        slug: "Borneol",
        name: "Borneol",
        abbreviation: "Borneol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₈O",
        molecularWeight: "154.25 g/mol",
        boilingPoint: "213°C (415°F)",
        cas: "507-70-0",
        psychoactive: false,
        description: "Borneol is a bicyclic monoterpene alcohol with a sharp, camphor-like aroma that has been used in traditional Chinese medicine for thousands of years as a flavoring and medicinal herb. In cannabis, borneol typically appears in minor amounts and is more common in landrace Asian varieties. Pharmacologically, borneol is notable for its ability to enhance the penetration of other compounds across the blood-brain barrier, a property actively studied for drug delivery applications. It also demonstrates analgesic, anti-inflammatory, and sedative properties, and has been traditionally used as a cardiac tonic.",
        effects: ["Analgesic", "Anti-inflammatory", "Sedative", "BBB penetration enhancer", "Antibacterial"],
        aroma: ["Camphor", "Mint", "Woody", "Earthy"],
        foundIn: ["Borneo camphor tree", "Ginger", "Thyme", "Rosemary"],
        receptors: null,
        researchAreas: ["Drug delivery", "Pain", "Cardiac health"],
        relatedCompounds: ["Camphene", "fenchone", "Isoborneol"],
        accentColor: "#78350f",
    },

    Sabinene: {
        slug: "Sabinene",
        name: "Sabinene",
        abbreviation: "Sabinene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆",
        molecularWeight: "136.23 g/mol",
        boilingPoint: "163°C (325°F)",
        cas: "3387-41-5",
        psychoactive: false,
        description: "Sabinene is a bicyclic monoterpene with a spicy, woody, pine-like aroma with hints of citrus. It is one of the main aroma compounds in black pepper and Norwegian spruce and is found in many spice and herb essential oils. In cannabis, sabinene contributes woody and spicy background notes. It has demonstrated antioxidant activity in various food systems and exhibits antimicrobial properties. Some early research suggests anti-inflammatory potential, though its pharmacological profile is less studied than more prominent terpenes.",
        effects: ["Antioxidant", "Antimicrobial", "Anti-inflammatory"],
        aroma: ["Spicy", "Woody", "Pine", "Citrus"],
        foundIn: ["Black pepper", "Norwegian spruce", "Carrot seed", "Oak"],
        receptors: null,
        researchAreas: ["Antioxidant", "Antimicrobial"],
        relatedCompounds: ["beta_pinene", "alpha_pinene", "Camphene"],
        accentColor: "#166534",
    },

    delta_3_carene: {
        slug: "delta_3_carene",
        name: "Delta-3-Carene",
        abbreviation: "Δ3-Carene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆",
        molecularWeight: "136.23 g/mol",
        boilingPoint: "168°C (334°F)",
        cas: "13466-78-9",
        psychoactive: false,
        description: "Delta-3-carene is a bicyclic monoterpene with a sweet, earthy, piney aroma with slight citrus overtones. It is a major component of pine resin and is found in many evergreen trees. In cannabis, delta-3-carene has a reputation for causing dry mouth and dry eyes — properties associated with its purported ability to dry out bodily fluids. It has demonstrated bone stimulation properties in preclinical studies, promoting the growth and repair of bone tissue, making it of potential interest in osteoporosis research. It is also studied for antifungal properties.",
        effects: ["Bone growth stimulation", "Antifungal", "Anti-inflammatory"],
        aroma: ["Sweet", "Earthy", "Pine", "Citrus"],
        foundIn: ["Pine trees", "Cedar", "Rosemary", "Basil"],
        receptors: null,
        researchAreas: ["Bone health", "Antifungal"],
        relatedCompounds: ["alpha_pinene", "Sabinene", "Camphene"],
        accentColor: "#166534",
    },

    Guaiol: {
        slug: "Guaiol",
        name: "Guaiol",
        abbreviation: "Guaiol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₆O",
        molecularWeight: "222.37 g/mol",
        boilingPoint: "~288°C",
        cas: "489-86-1",
        psychoactive: false,
        description: "Guaiol is a sesquiterpene alcohol with a woody, rose-like, piney aroma. It is found primarily in guaiacum wood and cypress pine and contributes a grounding, earthy character to cannabis strains where it appears. Guaiol has demonstrated antimicrobial activity, particularly against Staphylococcus aureus strains, and has shown anti-inflammatory properties in preclinical research. Some research has indicated potential cytotoxic effects against cancer cell lines. Guaiol is more commonly found in non-cannabis botanical sources and its presence on a cannabis COA typically indicates distinctive genetic heritage.",
        effects: ["Antimicrobial", "Anti-inflammatory", "Antitumor"],
        aroma: ["Woody", "Rose", "Piney", "Earthy"],
        foundIn: ["Guaiacum wood", "Cypress pine", "Niaouli"],
        receptors: null,
        researchAreas: ["Antimicrobial", "Cancer", "Anti-inflammatory"],
        relatedCompounds: ["Nerolidol", "Bisabolol", "alpha_humulene"],
        accentColor: "#78350f",
    },

    Fenchol: {
        slug: "Fenchol",
        name: "Fenchol",
        abbreviation: "Fenchol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₈O",
        molecularWeight: "154.25 g/mol",
        boilingPoint: "200°C (392°F)",
        cas: "1632-73-1",
        psychoactive: false,
        description: "Fenchol (also called fenchyl alcohol) is a monoterpenoid alcohol with a camphor-like, earthy, slightly pine and citrus aroma. It is a characteristic component of basil essential oil and contributes to its distinctive scent. In cannabis, fenchol is typically present in minor amounts. It demonstrates antibacterial and antifungal activities and may have analgesic properties. Research published in 2021 identified fenchol as capable of activating olfactory receptor-51E2 (OR51E2) in the gut, which appears to play a role in reducing beta-amyloid accumulation — a finding of potential significance in Alzheimer's disease research.",
        effects: ["Antibacterial", "Antifungal", "Analgesic", "Potential neuroprotective"],
        aroma: ["Camphor", "Earthy", "Pine", "Citrus"],
        foundIn: ["Basil", "Fennel", "Cannabis"],
        receptors: null,
        researchAreas: ["Alzheimer's disease", "Antibacterial", "Antifungal"],
        relatedCompounds: ["fenchone", "Borneol", "Camphene"],
        accentColor: "#0369a1",
    },

    Farnesene: {
        slug: "Farnesene",
        name: "Farnesene",
        abbreviation: "Farnesene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "~124°C (low pressure)",
        cas: "18794-84-8",
        psychoactive: false,
        description: "Farnesene is a sesquiterpene with a fruity, herbal, green aroma and exists in multiple isomeric forms. Alpha-farnesene is responsible for the characteristic scent of fresh apple peel. In plants, farnesene functions as an alarm pheromone — aphids release it when attacked by predators, triggering escape responses in neighboring aphids. In cannabis, farnesene contributes green, fruity, and herbal notes and has shown antimicrobial and anti-inflammatory properties. It is also being explored as a biofuel precursor and as a green chemistry feedstock.",
        effects: ["Antimicrobial", "Anti-inflammatory", "Analgesic"],
        aroma: ["Fruity", "Herbal", "Green apple", "Floral"],
        foundIn: ["Apple peel", "Ginger", "Chamomile", "Turmeric"],
        receptors: null,
        researchAreas: ["Antimicrobial", "Biofuel", "Inflammation"],
        relatedCompounds: ["Nerolidol", "Bisabolol", "alpha_humulene"],
        accentColor: "#15803d",
    },

    Elemene: {
        slug: "Elemene",
        name: "Elemene",
        abbreviation: "Elemene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "~234°C",
        cas: "29873-99-2",
        psychoactive: false,
        description: "Beta-elemene is a sesquiterpene found in many essential oils including celery, mints, and ginger. It has attracted significant attention in oncology research, particularly in China where an elemene-based injectable formulation (beta-elemene injection) has been approved as a cancer adjuvant therapy for over two decades. Multiple studies have demonstrated its ability to induce apoptosis and inhibit proliferation in various cancer cell lines through CDK inhibition and mitochondrial pathway activation. In cannabis, elemene is typically a minor constituent contributing to earthy, herbaceous notes.",
        effects: ["Antitumor", "Anti-inflammatory", "Antimicrobial"],
        aroma: ["Earthy", "Herbaceous", "Spicy", "Slightly citrus"],
        foundIn: ["Celery", "Ginger", "Myrrh", "Curcuma"],
        receptors: null,
        researchAreas: ["Cancer treatment", "Inflammation", "Antimicrobial"],
        relatedCompounds: ["gamma_elemene", "Farnesene", "Bisabolol"],
        accentColor: "#78350f",
    },

    Isoborneol: {
        slug: "Isoborneol",
        name: "Isoborneol",
        abbreviation: "Isoborneol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₈O",
        molecularWeight: "154.25 g/mol",
        boilingPoint: "~212°C",
        cas: "124-76-5",
        psychoactive: false,
        description: "Isoborneol is the endo-isomer of borneol, sharing the same molecular formula but with an axial rather than equatorial hydroxyl group. It has a similar camphor-like, woody aroma to borneol but is distinct in its biological activity profile. Isoborneol has demonstrated notably strong antiviral activity, particularly against herpes simplex virus (HSV-1), with some studies suggesting efficacy comparable to acyclovir. It also shows antibacterial and anti-inflammatory properties. In cannabis, isoborneol is a rare trace terpene.",
        effects: ["Antiviral", "Antibacterial", "Anti-inflammatory"],
        aroma: ["Camphor", "Woody", "Earthy"],
        foundIn: ["Mugwort", "Valerian", "Thyme"],
        receptors: null,
        researchAreas: ["Antiviral (herpes)", "Antibacterial"],
        relatedCompounds: ["Borneol", "Camphene", "fenchone"],
        accentColor: "#78350f",
    },

    Isopulegol: {
        slug: "Isopulegol",
        name: "Isopulegol",
        abbreviation: "Isopulegol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₈O",
        molecularWeight: "154.25 g/mol",
        boilingPoint: "~212°C",
        cas: "89-79-2",
        psychoactive: false,
        description: "Isopulegol is a natural monoterpenoid alcohol with a fresh, minty, slightly floral aroma. It is a biosynthetic precursor to menthol in peppermint and other mentha species. Pharmacologically, isopulegol is one of the most studied terpenes for gastroprotective applications: it has demonstrated significant protective effects against gastric ulcers in animal models through reduction of acid secretion and enhancement of mucus barriers. It also shows potent anxiolytic and anticonvulsant properties through GABA-A receptor modulation, similar to linalool.",
        effects: ["Gastroprotective", "Anxiolytic", "Anticonvulsant", "Anti-inflammatory"],
        aroma: ["Minty", "Fresh", "Floral", "Slightly sweet"],
        foundIn: ["Pennyroyal", "Peppermint", "Lemon eucalyptus"],
        receptors: null,
        researchAreas: ["Gastric ulcer", "Anxiety", "Epilepsy"],
        relatedCompounds: ["Menthol", "Linalool", "terpineol"],
        accentColor: "#0369a1",
    },

    Menthol: {
        slug: "Menthol",
        name: "Menthol",
        abbreviation: "Menthol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₂₀O",
        molecularWeight: "156.27 g/mol",
        boilingPoint: "212°C (414°F)",
        cas: "2216-51-5",
        psychoactive: false,
        description: "Menthol is arguably the most commercially familiar terpene in the world, widely used in pharmaceuticals, cosmetics, food, and tobacco products. It activates cold-sensitive TRPM8 receptors, producing the characteristic cooling sensation without actually lowering temperature. In cannabis, menthol is rare and typically occurs only in trace amounts or specialized cultivars. Its pharmacological profile is well established: analgesic (topical and oral), antibacterial, antiviral, and decongestant. Menthol also enhances transdermal absorption, making it relevant to topical cannabis formulations.",
        effects: ["Cooling sensation", "Analgesic", "Decongestant", "Antibacterial", "Antiviral"],
        aroma: ["Minty", "Cool", "Fresh"],
        foundIn: ["Peppermint", "Spearmint", "Corn mint"],
        receptors: null,
        researchAreas: ["Pain relief", "Respiratory health", "Topical delivery", "Antiviral"],
        relatedCompounds: ["Isopulegol", "Pulegone", "Eucalyptol"],
        accentColor: "#0369a1",
    },

    Pulegone: {
        slug: "Pulegone",
        name: "Pulegone",
        abbreviation: "Pulegone",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₀H₁₆O",
        molecularWeight: "152.23 g/mol",
        boilingPoint: "224°C (435°F)",
        cas: "89-82-7",
        psychoactive: false,
        description: "Pulegone is a naturally occurring ketone-terpene most abundantly found in pennyroyal mint and catnip. It has a strong, fresh, minty aroma with camphor and peppermint notes. In cannabis it is quite rare. Pulegone is of pharmacological interest for its acetylcholinesterase inhibitory activity — the same target as some Alzheimer's medications — and has been studied for memory-enhancement properties. It also demonstrates insecticidal and antibacterial activity. However, pulegone has hepatotoxic potential at high doses and is restricted in food flavorings in some jurisdictions.",
        effects: ["Acetylcholinesterase inhibition", "Insecticidal", "Antibacterial", "Memory enhancement"],
        aroma: ["Minty", "Camphor", "Peppermint", "Fresh"],
        foundIn: ["Pennyroyal", "Catnip", "Spearmint"],
        receptors: null,
        researchAreas: ["Alzheimer's disease", "Memory", "Insecticide"],
        relatedCompounds: ["Menthol", "Isopulegol", "fenchone"],
        accentColor: "#0369a1",
    },

    Phytol: {
        slug: "Phytol",
        name: "Phytol",
        abbreviation: "Phytol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₂₀H₄₀O",
        molecularWeight: "296.53 g/mol",
        boilingPoint: "~204°C",
        cas: "150-86-7",
        psychoactive: false,
        description: "Phytol is a diterpene alcohol derived from the hydrolysis of chlorophyll — making it one of the few terpenes that is a direct breakdown product of a plant's photosynthetic machinery. Its aroma is faint, grassy, and balsamic. In cannabis, phytol is present in trace amounts following the natural breakdown of chlorophyll during curing and drying. Phytol has been identified as an activator of the PPAR-α nuclear receptor pathway, giving it anti-inflammatory and immunosuppressive effects. It also demonstrates anxiolytic and anticonvulsant properties in rodent models through enhancement of GABA transmission.",
        effects: ["Anti-inflammatory", "Immunosuppressive", "Anxiolytic", "Anticonvulsant"],
        aroma: ["Grassy", "Balsamic", "Faint floral"],
        foundIn: ["Chlorophyll breakdown", "Green tea", "Alfalfa"],
        receptors: null,
        researchAreas: ["Inflammation", "Anxiety", "Epilepsy", "Immunology"],
        relatedCompounds: ["Geraniol", "Linalool", "Nerolidol"],
        accentColor: "#15803d",
    },

    alpha_bisabolene: {
        slug: "alpha_bisabolene",
        name: "Alpha-Bisabolene",
        abbreviation: "α-Bisabolene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "~125°C (low pressure)",
        cas: "17627-44-0",
        psychoactive: false,
        description: "Alpha-bisabolene is a sesquiterpene hydrocarbon closely related to alpha-bisabolol and found in the same plant sources — particularly chamomile and candeia trees. Unlike bisabolol, it lacks a hydroxyl group. It contributes a spicy, balsamic, faintly citrus aroma. Research on alpha-bisabolene specifically is limited compared to bisabolol; however, it has been studied for anti-tumor properties (particularly against colorectal cancer cell lines) and demonstrates anti-inflammatory activity. In cannabis terpene profiles, it is present in minor amounts in certain cultivars.",
        effects: ["Antitumor", "Anti-inflammatory"],
        aroma: ["Spicy", "Balsamic", "Citrus", "Woody"],
        foundIn: ["Chamomile", "Candeia tree", "Bisabolene-rich cannabis varieties"],
        receptors: null,
        researchAreas: ["Cancer", "Inflammation"],
        relatedCompounds: ["Bisabolol", "beta_bisabolene", "alpha_humulene"],
        accentColor: "#78350f",
    },

    beta_bisabolene: {
        slug: "beta_bisabolene",
        name: "Beta-Bisabolene",
        abbreviation: "β-Bisabolene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "~124°C (low pressure)",
        cas: "495-61-4",
        psychoactive: false,
        description: "Beta-bisabolene is a sesquiterpene found in many essential oils including turmeric and ginger. It has a warm, sweet, woody, spicy aroma. Beta-bisabolene has demonstrated antihistamine properties and has been studied for antifungal and antibacterial effects. Similar to alpha-bisabolene, it has shown antitumor activity in preliminary in vitro studies. In cannabis, beta-bisabolene is present in minor to trace amounts in selected cultivars, contributing warm woody-spicy notes to the aromatic profile.",
        effects: ["Antihistamine", "Antifungal", "Antibacterial", "Antitumor"],
        aroma: ["Warm", "Woody", "Spicy", "Sweet"],
        foundIn: ["Turmeric", "Ginger", "Chamomile", "Myrrh"],
        receptors: null,
        researchAreas: ["Allergy", "Antifungal", "Cancer"],
        relatedCompounds: ["alpha_bisabolene", "Bisabolol", "Farnesene"],
        accentColor: "#78350f",
    },

    Cedrene: {
        slug: "Cedrene",
        name: "Cedrene",
        abbreviation: "Cedrene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "~262°C",
        cas: "469-61-4",
        psychoactive: false,
        description: "Alpha-cedrene is a sesquiterpene primarily associated with cedar trees, contributing to the characteristic woody, earthy, warm aroma of cedarwood oil. In cannabis, cedrene contributes deep, woody, earthy notes that add complexity to a terpene profile. It demonstrates insecticidal properties (used in natural pest repellents) and has shown anti-inflammatory activity in preclinical models. Cedrene is also used extensively as a fragrance ingredient in the perfumery industry for its longevity and depth.",
        effects: ["Insecticidal", "Anti-inflammatory", "Antibacterial"],
        aroma: ["Cedar", "Woody", "Earthy", "Warm"],
        foundIn: ["Cedarwood", "Juniper", "Cypress"],
        receptors: null,
        researchAreas: ["Insect repellent", "Inflammation"],
        relatedCompounds: ["Selina_diene", "Guaiol", "alpha_humulene"],
        accentColor: "#78350f",
    },

    Bergamotene: {
        slug: "Bergamotene",
        name: "Bergamotene",
        abbreviation: "Bergamotene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "~125°C (low pressure)",
        cas: "17699-05-7",
        psychoactive: false,
        description: "Alpha-bergamotene is a sesquiterpene contributing to the distinctive aroma of bergamot oil and some cannabis cultivars. It has a fresh, spicy, citrusy, slightly woody aroma. In cannabis and other plants, bergamotene serves as an insect-attractant compound during pollination and has also been studied as a natural repellent for certain herbivorous insects. Its pharmacological properties in human medicine are limited in research, though preliminary studies indicate anti-inflammatory potential.",
        effects: ["Insect attraction/repellent", "Anti-inflammatory"],
        aroma: ["Citrus", "Spicy", "Fresh", "Woody"],
        foundIn: ["Bergamot", "Ylang-ylang", "Some cannabis cultivars"],
        receptors: null,
        researchAreas: ["Plant defense chemistry", "Inflammation"],
        relatedCompounds: ["Limonene", "Farnesene", "Ocimene"],
        accentColor: "#ca8a04",
    },

    Aristolene: {
        slug: "Aristolene",
        name: "Aristolene",
        abbreviation: "Aristolene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "~260°C",
        cas: "6831-16-9",
        psychoactive: false,
        description: "Aristolene is a rare bicyclic sesquiterpene found in some cannabis cultivars and various aromatic plants. Its aroma is described as woody, earthy, and slightly spicy. Research on aristolene is minimal; it is primarily detected and identified in essential oil analyses. Some studies have identified aristolene in plants with traditional antimicrobial and anti-inflammatory uses, suggesting it may contribute to these properties, though direct pharmacological data on aristolene itself is sparse. Its presence on a cannabis COA reflects the genetic diversity of the cultivar.",
        effects: ["Potential antimicrobial", "Potential anti-inflammatory"],
        aroma: ["Woody", "Earthy", "Spicy"],
        foundIn: ["Some cannabis cultivars", "Aristolochia species"],
        receptors: null,
        researchAreas: ["Antimicrobial (preliminary)"],
        relatedCompounds: ["Cedrene", "Selina_diene", "beta_maaliene"],
        accentColor: "#78350f",
    },

    Selina_diene: {
        slug: "Selina_diene",
        name: "Selina-Diene",
        abbreviation: "Selina-Diene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: false,
        description: "Selina-diene (specifically delta-selina-3,7(11)-diene) is a sesquiterpene found in various aromatic plants and in some cannabis cultivars. It contributes earthy, woody aromatic notes. Research on selina-diene directly is very limited; it is primarily studied as a component of complex essential oil mixtures. It is related to the selinene family of sesquiterpenes found in various umbellifers (carrot family plants). Its pharmacological properties are inferred from related compounds but are not well characterized independently.",
        effects: ["Potential antimicrobial", "Aromatic constituent"],
        aroma: ["Earthy", "Woody"],
        foundIn: ["Some cannabis cultivars", "Apiaceae family plants"],
        receptors: null,
        researchAreas: ["Essential oil chemistry"],
        relatedCompounds: ["Cedrene", "Aristolene", "gamma_elemene"],
        accentColor: "#78350f",
    },

    trans_sesquisabinene_hydrate: {
        slug: "trans_sesquisabinene_hydrate",
        name: "Trans-Sesquisabinene Hydrate",
        abbreviation: "TSSH",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₆O",
        molecularWeight: "222.37 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: false,
        description: "Trans-sesquisabinene hydrate is a rare sesquiterpene alcohol occasionally detected in cannabis terpene analyses. It is structurally related to sabinene and contributes an earthy, slightly spicy aromatic character. This compound is primarily found in trace amounts in cannabis and certain essential oils. Pharmacological research specific to trans-sesquisabinene hydrate is extremely limited; it is generally considered a minor constituent with undefined therapeutic significance.",
        effects: ["Potential antimicrobial"],
        aroma: ["Earthy", "Spicy"],
        foundIn: ["Some cannabis cultivars", "Trace in various essential oils"],
        receptors: null,
        researchAreas: ["Essential oil chemistry"],
        relatedCompounds: ["Sabinene", "Aristolene", "Selina_diene"],
        accentColor: "#78350f",
    },

    gamma_elemene: {
        slug: "gamma_elemene",
        name: "Gamma-Elemene",
        abbreviation: "γ-Elemene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "~230°C",
        cas: "29873-99-2",
        psychoactive: false,
        description: "Gamma-elemene is a sesquiterpene isomer of beta-elemene with similar aromatic and pharmacological properties. Like its beta isomer, gamma-elemene has been investigated for antitumor properties. In traditional Chinese medicine, elemene-containing herbs have been used for cancer-related conditions for centuries, and gamma-elemene has demonstrated cytotoxic activity against several cancer cell lines including hepatocellular and leukemia cells. In cannabis, gamma-elemene is a rare trace terpene that contributes earthy, herbaceous notes.",
        effects: ["Antitumor", "Anti-inflammatory"],
        aroma: ["Earthy", "Herbaceous", "Spicy"],
        foundIn: ["Myrrh", "Curcuma", "Some cannabis cultivars"],
        receptors: null,
        researchAreas: ["Cancer", "Inflammation"],
        relatedCompounds: ["Elemene", "Farnesene", "Aristolene"],
        accentColor: "#78350f",
    },

    beta_maaliene: {
        slug: "beta_maaliene",
        name: "Beta-Maaliene",
        abbreviation: "β-Maaliene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: false,
        description: "Beta-maaliene is a rare bicyclic sesquiterpene occasionally identified in cannabis and a limited number of other aromatic plants. Its aroma is earthy and woody. Research on beta-maaliene is essentially absent from the scientific literature; it is primarily documented through essential oil composition studies. Its presence on a cannabis COA is indicative of the full biochemical complexity of the cannabis plant's terpene biosynthesis pathways, even when individual compounds lack characterized pharmacology.",
        effects: ["Unknown — research absent"],
        aroma: ["Earthy", "Woody"],
        foundIn: ["Some cannabis cultivars", "Trace in complex essential oils"],
        receptors: null,
        researchAreas: ["Essential oil chemistry"],
        relatedCompounds: ["Aristolene", "Selina_diene", "Cedrene"],
        accentColor: "#78350f",
    },

    Bulnesol: {
        slug: "Bulnesol",
        name: "Bulnesol",
        abbreviation: "Bulnesol",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₆O",
        molecularWeight: "222.37 g/mol",
        boilingPoint: "~309°C",
        cas: "22451-73-6",
        psychoactive: false,
        description: "Bulnesol is a sesquiterpene alcohol found in guaiacum wood and some cannabis cultivars. It has a woody, slightly rose-like, earthy aroma. Pharmacologically, bulnesol has shown antibacterial activity and demonstrates limited anti-inflammatory properties in preclinical studies. It is related to guaiol and contributes similar grounding, woody qualities to a terpene profile. Like other rare sesquiterpene alcohols on cannabis COAs, its significance is primarily compositional — indicating full biochemical diversity of the cultivar — rather than known therapeutic.",
        effects: ["Antibacterial", "Anti-inflammatory"],
        aroma: ["Woody", "Earthy", "Rose-like"],
        foundIn: ["Guaiacum wood", "Some cannabis cultivars"],
        receptors: null,
        researchAreas: ["Antibacterial", "Anti-inflammatory"],
        relatedCompounds: ["Guaiol", "Nerolidol", "Bisabolol"],
        accentColor: "#78350f",
    },

    Chamigrene: {
        slug: "Chamigrene",
        name: "Chamigrene",
        abbreviation: "Chamigrene",
        type: "terpene",
        subtype: "terpene",
        formula: "C₁₅H₂₄",
        molecularWeight: "204.35 g/mol",
        boilingPoint: "—",
        cas: "—",
        psychoactive: false,
        description: "Chamigrene is a bicyclic sesquiterpene found in chamomile oil and certain marine organisms as well as some cannabis cultivars. It contributes a distinctive earthy, woody aromatic character. Some chamigrene isomers have been isolated from red algae, where they demonstrate antimicrobial and antitumor activity. Research specifically on chamigrene in cannabis and terrestrial plant contexts is very limited. Its detection on a cannabis COA represents the plant's complex secondary metabolite diversity rather than established therapeutic application.",
        effects: ["Potential antimicrobial", "Potential antitumor"],
        aroma: ["Earthy", "Woody"],
        foundIn: ["Chamomile", "Red algae", "Some cannabis cultivars"],
        receptors: null,
        researchAreas: ["Antimicrobial (marine sources)", "Antitumor"],
        relatedCompounds: ["Aristolene", "beta_maaliene", "Cedrene"],
        accentColor: "#78350f",
    },
};

export default compoundData;
